import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton } from '@mui/material';
import { ChevronLeft, Copy, Play, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip, ConfirmationDialog, RouterLink } from 'src/components';
import { queuePromptDraft, requestPromptDraft } from 'src/components/ai/chat/chat-state';
import { AlineButton } from 'src/components/generic';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai';
import { useIsOrgAdmin } from 'src/gql';
import { useDispatchSuccessSnack } from 'src/helpers';
import { useDeletePlaybook, useDuplicatePlaybook, useUpdatePlaybook } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import { PlaybookTextarea } from './playbook-textarea';
import { createRunPlaybookPrompt } from './run-playbook-prompt';
import type { PlaybookDetail } from './types';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
  },
  backButton: {
    color: theme.appColors.iconsType,
  },
  description: {
    color: theme.appColors.secondaryType,
    fontSize: 14,
    gridColumn: 2,
    width: '100%',
  },
  /**
   * The back button sits in its own column, so the title and the description
   * under it share one left edge. Centering is per row, which lines the
   * chevron up with the title however tall either happens to be.
   */
  header: {
    alignItems: 'center',
    columnGap: theme.spacing(0.5),
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    rowGap: theme.spacing(0.5),
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
  },
  titleDisplay: {
    color: theme.appColors.iconsType,
    fontSize: 22,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleInput: {
    fontSize: 22,
    fontWeight: 700,
  },
  titlePlaceholder: {
    color: theme.appColors.secondaryType,
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
}));

interface PlaybookHeaderProps {
  playbook: PlaybookDetail;
  disabled?: boolean;
}

export function PlaybookHeader({ disabled = false, playbook }: PlaybookHeaderProps): JSX.Element {
  const { classes } = useStyles();
  const { openDrawer } = useGlobalDrawer();
  const { updatePlaybook } = useUpdatePlaybook();
  const handleRun = useCallback(() => {
    const prompt = createRunPlaybookPrompt({
      playbookId: playbook.id,
      playbookTitle: playbook.title,
    });
    const isLeavingExistingThread = Boolean(aiThreadIdVar());
    aiThreadIdVar(null);
    openDrawer();
    if (isLeavingExistingThread) queuePromptDraft(prompt);
    else requestPromptDraft(prompt);
  }, [openDrawer, playbook.id, playbook.title]);

  return (
    <div className={classes.header}>
      <IconButton
        className={classes.backButton}
        component={RouterLink}
        data-cy={Select.playbooks.detailBack}
        size="small"
        to={link.PlaybooksIndex()}
      >
        <ChevronLeft size={20} />
      </IconButton>
      <div className={classes.titleRow}>
        <div className={classes.title} data-cy={Select.playbooks.detailTitle}>
          <InlineEditField
            classes={{
              display: classes.titleDisplay,
              input: classes.titleInput,
              placeholder: classes.titlePlaceholder,
            }}
            disabled={disabled}
            editTooltip="Rename playbook"
            iconSize={16}
            /**
             * An untitled playbook (typically one just created) opens ready to
             * be named. Mount-only: the header mounts once the playbook has loaded.
             */
            initiallyEditing={!playbook.title}
            placeholder="Type a playbook title..."
            textPlaceholder="Untitled Playbook"
            value={playbook.title}
            onSave={title => updatePlaybook({ playbookId: playbook.id, props: { title } })}
          />
        </div>
        <DuplicatePlaybookButton disabled={disabled} playbookId={playbook.id} />
        <DeletePlaybookButton disabled={disabled} playbook={playbook} />
        <AlineButton
          color="primary"
          disabled={disabled}
          size="small"
          startIcon={<Play size={14} />}
          variant="contained"
          onClick={handleRun}
        >
          Run
        </AlineButton>
      </div>
      <PlaybookTextarea
        className={classes.description}
        dataCy={Select.playbooks.description}
        disabled={disabled}
        placeholder="Type a playbook description..."
        value={playbook.description}
        onPersist={description =>
          updatePlaybook({ playbookId: playbook.id, props: { description } })
        }
      />
    </div>
  );
}

function DuplicatePlaybookButton({
  disabled,
  playbookId,
}: {
  disabled: boolean;
  playbookId: string;
}): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { duplicatePlaybook, loading } = useDuplicatePlaybook();
  const dispatchDuplicatedSnack = useDispatchSuccessSnack({ message: 'Playbook duplicated.' });
  const handleDuplicate = useCallback(async () => {
    const { data } = await duplicatePlaybook(playbookId);
    if (!data) return;

    dispatchDuplicatedSnack();
    navigate(link.PlaybooksDetail({ playbookId: data.duplicatePlaybook.id }));
  }, [dispatchDuplicatedSnack, duplicatePlaybook, navigate, playbookId]);

  return (
    <AlineTooltip arrow placement="top" title="Duplicate playbook">
      <IconButton
        aria-label="Duplicate playbook"
        className={classes.actionButton}
        data-cy={Select.playbooks.duplicateButton}
        disabled={disabled || loading}
        size="small"
        onClick={handleDuplicate}
      >
        <Copy size={16} />
      </IconButton>
    </AlineTooltip>
  );
}

function DeletePlaybookButton({
  disabled,
  playbook,
}: {
  disabled: boolean;
  playbook: PlaybookDetail;
}): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { deletePlaybook } = useDeletePlaybook();
  const { currentUser, isOrgAdmin } = useIsOrgAdmin();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dispatchDeletedSnack = useDispatchSuccessSnack({ message: 'Playbook deleted.' });
  const canDelete = isOrgAdmin || currentUser?.id === playbook.createdBy?.id;
  const openDeleteConfirm = useCallback(() => setConfirmOpen(true), []);
  const handleDeleteConfirmed = useCallback(async () => {
    const { data } = await deletePlaybook(playbook.id);
    if (!data) return;

    dispatchDeletedSnack();
    navigate(link.PlaybooksIndex());
  }, [deletePlaybook, dispatchDeletedSnack, navigate, playbook.id]);

  return (
    <>
      <AlineTooltip
        arrow
        placement="top"
        title={canDelete ? 'Delete playbook' : 'Only the playbook author or an admin can delete it'}
      >
        {/* Wrapped so the tooltip still works while the button is disabled. */}
        <span>
          <IconButton
            aria-label="Delete playbook"
            className={classes.actionButton}
            data-cy={Select.playbooks.detailDelete}
            disabled={disabled || !canDelete}
            size="small"
            onClick={openDeleteConfirm}
          >
            <Trash2 size={16} />
          </IconButton>
        </span>
      </AlineTooltip>
      <ConfirmationDialog
        message="Are you sure you want to delete this playbook? This action cannot be undone."
        open={confirmOpen}
        title="Delete playbook?"
        onClickPrimary={handleDeleteConfirmed}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
